import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { Card } from '../Card';
import { SystemConfig } from '../../data/types';
import SecuritySettingsTab from '../system-config/SecuritySettingsTab';
import SkipAndMoveTab from '../system-config/SkipAndMoveTab';
import LearningProgressionTab from '../system-config/LearningProgressionTab';
import IntegrationTab from '../system-config/IntegrationTab';
import ConcurrentAccessTab from '../system-config/ConcurrentAccessTab';
import FeatureTogglesTab from '../system-config/FeatureTogglesTab';
import PageContainer from '../layouts/PageContainer';

const SuperAdminSystemConfigScreen: React.FC = () => {
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [activeTab, setActiveTab] = useState('security');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const configData = await dataService.getSystemConfig();
        setSystemConfig(configData);
      } catch (error) {
        console.error('Error fetching system configuration:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSaveConfig = async (updatedConfig: Partial<SystemConfig>) => {
    if (!systemConfig) return;

    try {
      setSaveStatus('saving');
      const mergedConfig = { ...systemConfig, ...updatedConfig };
      await dataService.updateSystemConfig(mergedConfig);
      setSystemConfig(mergedConfig);
      setSaveStatus('success');
      
      // Reset status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving system configuration:', error);
      setSaveStatus('error');
      
      // Reset status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Tab navigation
  const tabs = [
    { id: 'security', label: 'Security Settings' },
    { id: 'skipAndMove', label: 'Skip and Move to Game' },
    { id: 'learningProgression', label: 'Learning Progression' },
    { id: 'integration', label: 'Integration' },
    { id: 'concurrentAccess', label: 'Concurrent Access' },
    { id: 'featureToggles', label: 'Feature Toggles' }
  ];

  if (loading) {
    return (
      <PageContainer title="System Configuration" userRole="SuperAdmin">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  if (!systemConfig) {
    return (
      <PageContainer title="System Configuration" userRole="SuperAdmin">
        <div className="bg-red-50 p-4 rounded-md">
          <h2 className="text-lg font-medium text-red-800">Error</h2>
          <p className="text-red-700">Failed to load system configuration. Please try again later.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="System Configuration" userRole="SuperAdmin">
      {saveStatus === 'success' && (
        <div className="mb-4 p-3 rounded bg-green-50 text-green-700">
          Configuration saved successfully.
        </div>
      )}
      
      {saveStatus === 'error' && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700">
          Error saving configuration. Please try again.
        </div>
      )}
      
      <Card className="bg-white shadow-sm">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'security' && (
            <SecuritySettingsTab 
              config={systemConfig} 
              onSave={handleSaveConfig} 
              saving={saveStatus === 'saving'} 
            />
          )}
          
          {activeTab === 'skipAndMove' && (
            <SkipAndMoveTab 
              config={systemConfig} 
              onSave={handleSaveConfig} 
              saving={saveStatus === 'saving'} 
            />
          )}
          
          {activeTab === 'learningProgression' && (
            <LearningProgressionTab 
              config={systemConfig} 
              onSave={handleSaveConfig} 
              saving={saveStatus === 'saving'} 
            />
          )}
          
          {activeTab === 'integration' && (
            <IntegrationTab 
              config={systemConfig} 
              onSave={handleSaveConfig} 
              saving={saveStatus === 'saving'} 
            />
          )}
          
          {activeTab === 'concurrentAccess' && (
            <ConcurrentAccessTab 
              config={systemConfig} 
              onSave={handleSaveConfig} 
              saving={saveStatus === 'saving'} 
            />
          )}
          
          {activeTab === 'featureToggles' && (
            <FeatureTogglesTab 
              config={systemConfig} 
              onSave={handleSaveConfig} 
              saving={saveStatus === 'saving'} 
            />
          )}
        </div>
      </Card>
    </PageContainer>
  );
};

export default SuperAdminSystemConfigScreen;
